import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, unref as _unref } from "vue"

const _hoisted_1 = {
  key: 1,
  class: "mb-2 pa-2 bg-blue-grey-lighten-5 rounded text-center"
}
const _hoisted_2 = { class: "text-primary" }
const _hoisted_3 = {
  class: "map-container",
  style: {"height":"400px"}
}
const _hoisted_4 = {
  key: 0,
  class: "mt-2 text-caption"
}
const _hoisted_5 = { class: "text-caption" }
const _hoisted_6 = { class: "mt-2" }
const _hoisted_7 = {
  key: 0,
  class: "text-caption text-grey"
}
const _hoisted_8 = ["onClick"]
const _hoisted_9 = { class: "font-weight-bold" }
const _hoisted_10 = { class: "text-center" }
const _hoisted_11 = {
  key: 0,
  class: "mt-3"
}
const _hoisted_12 = { class: "d-flex align-center mb-2" }
const _hoisted_13 = { key: 0 }
const _hoisted_14 = { key: 0 }
const _hoisted_15 = { class: "text-caption" }
const _hoisted_16 = { key: 0 }
const _hoisted_17 = { key: 1 }
const _hoisted_18 = { class: "text-caption" }
const _hoisted_19 = { class: "text-caption text-center mt-1" }

import { ref, computed, watch, type Ref } from 'vue';
import { v4 } from 'uuid';
import type { Map } from 'maplibre-gl';
import EsriMap from './components/EsriMap.vue';
import DateTimeRangeSelection from './date_time_range_selection/DateTimeRangeSelection.vue';
import PlotlyGraph from './components/plotly/PlotlyGraph.vue';
import RequestStatistics from './components/RequestStatistics.vue';
import TimeRangeStatusList from './components/TimeRangeStatusList.vue';
import InfoButton from './components/InfoButton.vue';
import { useTempoStore } from './stores/app';
import type { LatLngPair, UserDataset, PlotltGraphDataSet } from './types';
import type { RectBounds } from './esri/geometry';
import type { RequestStats, ParcelingMode } from './esri/services/TempoDataService';
import type { MoleculeType } from './esri/utils';
import { useMultiMarker } from './composables/maplibre/useMultiMarker';
import { EsriSampler } from './esri/services/sampling';
import { useEsriTimesteps } from './composables/useEsriTimesteps';
import { useUniqueTimeSelection } from './composables/useUniqueTimeSelection';
import { generateTimeRanges, type TimeRangeConfig, type TimeRangeConfigMultiple } from './date_time_range_selection/date_time_range_generators';
import type { MillisecondRange } from './types/datetime';
import { filterForAllowedDates } from './date_time_range_selection/filter_ranges';
import { nanmean } from './utils/array_operations/array_math';
import { userDatasetToPlotly } from './utils/data_converters';
// Store
interface TestRegion {
  id: string;
  name: string;
  size: string;
  geometry: RectBounds;
  color: string;
}

interface PresetTimeRange {
  id: string;
  name: string;
  description: string;
  ranges: { start: number; end: number }[];
  config?: TimeRangeConfig; // Optional config for complex patterns
}

// Test preset: August 2-4, 2023 with multiple times per day
// Some of these time windows may not have ESRI data

export default /*@__PURE__*/_defineComponent({
  __name: 'TestingEsri',
  setup(__props) {

const store = useTempoStore();

// Fetch ESRI timesteps directly (not from store)
const molecule = ref<MoleculeType>('no2');
const { esriTimesteps } = useEsriTimesteps(molecule, true);

// Use unique time selection composable
const {
  singleDateSelected,
  uniqueDays,
} = useUniqueTimeSelection(esriTimesteps);

// State
const showHelp = ref(false);
const map = ref<Map | null>(null);
const fetching = ref(false);
const requestStats = ref<RequestStats[]>([]);
const datasets = ref<UserDataset[]>([]);
const dateTimeKey = ref(0);

// ESRI Map state
const mapOpacity = ref(0.8);
const mapTimestamp = ref<number | null>(null);
const selectedTimeRangeForMap = ref<RequestStats | null>(null);

// Progress tracking
const totalRequests = ref(0);
const completedRequests = ref(0);
const fetchProgress = computed(() => 
  totalRequests.value > 0 ? (completedRequests.value / totalRequests.value) * 100 : 0
);

// Settings
const sampleCount = ref(30);
const rateLimitMs = ref(50);
const maxRetries503 = ref(1);

// Parceling configuration
const parcelingMode = ref<ParcelingMode>('smart');
const parcelingModeOptions = [
  { title: 'None (use as-is)', value: 'none' },
  { title: 'Default (fixed chunks)', value: 'default' },
  { title: 'Smart (optimize by timestamps)', value: 'smart' },
];
const defaultParcelSizeDays = ref(7); // days
const maxSamplesPerRequest = ref(5000);
const safetyMargin = ref(0.9);

// Time range tabs
const timeRangeTab = ref('presets');

// Map config
const homeState = ref({
  loc: [40.044, -98.789] as LatLngPair,
  zoom: 4,
  t: null as number | null
});

// Test regions - centered around central US
const predefinedRegions = ref<TestRegion[]>([
  {
    id: 'small',
    name: 'Small Region',
    size: '0.02° × 0.02°',
    geometry: {
      xmin: -98.8,
      ymin: 40.03,
      xmax: -98.78,
      ymax: 40.05
    },
    color: 'green'
  },
  {
    id: 'medium',
    name: 'Medium Region',
    size: '0.1° × 0.05°',
    geometry: {
      xmin: -98.85,
      ymin: 40.0,
      xmax: -98.75,
      ymax: 40.05
    },
    color: 'blue'
  },
  {
    id: 'large',
    name: 'Large Region',
    size: '1.25° × 1.0°',
    geometry: {
      xmin: -99.375,
      ymin: 39.55,
      xmax: -98.125,
      ymax: 40.55
    },
    color: 'orange'
  }
]);

const selectedRegion = ref<TestRegion | null>(null);

// Sample points display state
const showSamplePoints = ref<Record<string, boolean>>({
  small: false,
  medium: false,
  large: false
});

// Actual sample points display state
const showActualSamplePoints = ref<Record<string, boolean>>({
  small: false,
  medium: false,
  large: false
});

// Store sample locations for each region
const regionSampleLocations = ref<Record<string, Array<{ x: number; y: number }>>>({});

// Store actual sample locations from fetch results
const regionActualSampleLocations = ref<Record<string, Array<{ x: number; y: number }>>>({});

// Multi-marker composables for each region
const sampleMarkers: Record<string, ReturnType<typeof useMultiMarker>> = {};

// Multi-marker composables for actual sample points (red)
const actualSampleMarkers: Record<string, ReturnType<typeof useMultiMarker>> = {};

// Sampler for generating sample point locations
const sampler = ref<EsriSampler | null>(null);

// Preset time ranges
const testAugust2023Config: TimeRangeConfigMultiple = {
  type: 'multiple',
  dateRange: {
    start: new Date('2023-08-02T00:00:00.000Z'),
    end: new Date('2023-08-04T23:59:59.999Z')
  },
  years: undefined,
  months: undefined,
  weekdays: undefined,
  times: ['10 am', '12 pm', '2 pm', '4 pm', '6 pm'],
  toleranceHours: [0.5, 0.5]
};

// Generate the "All Available Data" preset with the specified pattern
const allDataConfig: TimeRangeConfigMultiple = {
  type: 'multiple',
  dateRange: {
    start: new Date('2023-01-01T00:00:00.000Z'),
    end: new Date('2025-12-31T23:59:59.999Z')
  },
  years: undefined,
  months: undefined,
  // [
  //   'January', 'February', 'March', 'April', 'May', 'June',
  //   'July', 'August', 'September', 'October', 'November', 'December'
  // ],
  weekdays: undefined,
  // [
  //   'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
  // ],
  times: undefined,
  toleranceHours: [0.5, 0.5]
};

const manyTimeRangesConfig: TimeRangeConfigMultiple = {
  type: 'multiple',
  dateRange: {
    start: new Date('2023-01-01T00:00:00.000Z'),
    end: new Date('2025-12-31T23:59:59.999Z')
  },
  years: undefined,
  months: [
    'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ],
  weekdays: [
    'Sunday', 'Monday', 'Tuesday', 'Thursday', 'Friday', 'Saturday'
  ],
  times: ['9 am', '11am', '1pm', '3pm'],
  toleranceHours: [0.5, 0.5]
};

// Student-realistic request: Summer mornings with discrete times
const studentSummerMorningsConfig: TimeRangeConfigMultiple = {
  type: 'multiple',
  dateRange: {
    start: new Date('2023-01-01T00:00:00.000Z'),
    end: new Date('2025-12-31T23:59:59.999Z')
  },
  years: undefined,
  months: ['June', 'July', 'August'],
  weekdays: undefined,
  times: ['8 am', '9 am', '10 am'],
  toleranceHours: [0.5, 0.5]
};

// Student-realistic request with smart parceling: Summer mornings with time range
const studentSummerMorningsSmartConfig: TimeRangeConfigMultiple = {
  type: 'multiple',
  dateRange: {
    start: new Date('2023-01-01T00:00:00.000Z'),
    end: new Date('2025-12-31T23:59:59.999Z')
  },
  years: undefined,
  months: ['June', 'July', 'August'],
  weekdays: undefined,
  times: ['8 am-11 am'],
  toleranceHours: undefined
};





// Computed preset time ranges based on actual available data
// These depend on the parcel size setting for proper parceling
const presetTimeRanges = computed<PresetTimeRange[]>(() => {
  // Get the last available timestamp from esriTimesteps
  const lastTimestamp = esriTimesteps.value.length > 0 
    ? esriTimesteps.value[esriTimesteps.value.length - 1] 
    : Date.now();
  
  const end = new Date(lastTimestamp);
  
  // Use defaultParcelSizeDays converted to milliseconds
  const parcelSizeMs = defaultParcelSizeDays.value * 24 * 60 * 60 * 1000;
  
  // Helper to create range configs with parceling
  const createRangeConfig = (daysBack: number): TimeRangeConfigMultiple => ({
    type: 'multiple',
    dateRange: {
      start: new Date(lastTimestamp - daysBack * 24 * 60 * 60 * 1000),
      end: end
    },
    years: undefined,
    months: undefined,
    weekdays: undefined,
    times: undefined,
    toleranceHours: undefined,
    parcel: false,
    parcelSize: parcelSizeMs
  });
  
  const configs = {
    lastWeek: createRangeConfig(7),
    lastMonth: createRangeConfig(30),
    last3Months: createRangeConfig(90),
    lastYear: createRangeConfig(365),
  };
  
  return [
    {
      id: 'last-week',
      name: 'Last Week',
      description: `Last 7 days of data`,
      ranges: generateTimeRanges(configs.lastWeek),
      config: configs.lastWeek
    },
    {
      id: 'last-month',
      name: 'Last Month',
      description: `Last 30 days of data`,
      ranges: generateTimeRanges(configs.lastMonth),
      config: configs.lastMonth
    },
    {
      id: 'last-3-months',
      name: 'Last 3 Months',
      description: `Last 90 days of data`,
      ranges: generateTimeRanges(configs.last3Months),
      config: configs.last3Months
    },
    {
      id: 'last-year',
      name: 'Last Year',
      description: `Last 365 days of data`,
      ranges: generateTimeRanges(configs.lastYear),
      config: configs.lastYear
    },
    {
      id: 'test-august-2023',
      name: 'Test: Aug 2-4, 2023',
      description: `3 days, 5 times/day (10am, 12pm, 2pm, 4pm, 6pm) ±30min`,
      ranges: generateTimeRanges({...testAugust2023Config, parcel: false, parcelSize: parcelSizeMs}, false),
      config: {...testAugust2023Config, parcel: false, parcelSize: parcelSizeMs}
    },
    {
      id: 'all-data-pattern',
      name: 'All Available Data (Pattern)',
      description: `2023-2025, All months/days`,
      ranges: filterForAllowedDates(generateTimeRanges({...allDataConfig, parcel: false, parcelSize: parcelSizeMs}, false), uniqueDays.value),
      config: {...allDataConfig, parcel: false, parcelSize: parcelSizeMs}
    },
    {
      id: 'many-ranges-pattern',
      name: 'Many Time Ranges (Pattern)',
      description: `2023-2025, Most months/days, 12pm/3pm/9pm ±30min`,
      ranges: filterForAllowedDates(generateTimeRanges({...manyTimeRangesConfig, parcel: false, parcelSize: parcelSizeMs}, false), uniqueDays.value),
      config: {...manyTimeRangesConfig, parcel: false, parcelSize: parcelSizeMs}
    },
    {
      id: 'student-summer-mornings',
      name: 'Student Request: Summer Mornings',
      description: `2023-2025, Jun-Aug, 8am/9am/10am ±30min`,
      ranges: filterForAllowedDates(generateTimeRanges({...studentSummerMorningsConfig, parcel: false, parcelSize: parcelSizeMs}, false), uniqueDays.value),
      config: {...studentSummerMorningsConfig, parcel: false, parcelSize: parcelSizeMs}
    },
    {
      id: 'student-summer-mornings-smart',
      name: 'Student Request: Summer Mornings (Smart)',
      description: `2023-2025, Jun-Aug, 8am-11am (time range)`,
      ranges: filterForAllowedDates(generateTimeRanges({...studentSummerMorningsSmartConfig, parcel: false, parcelSize: parcelSizeMs}, false), uniqueDays.value),
      config: {...studentSummerMorningsSmartConfig, parcel: false, parcelSize: parcelSizeMs}
    }
  ];
});

const selectedTimeRange = ref<PresetTimeRange | null>(null);

// Store custom time ranges created by the user
const customTimeRanges = ref<PresetTimeRange[]>([]);

// Computed: all available time ranges (presets + custom)
const allTimeRanges = computed(() => [...presetTimeRanges.value, ...customTimeRanges.value]);

// Computed: Actual sample count after grid adjustment
const actualSampleCount = computed(() => {
  if (!selectedRegion.value || !sampler.value) {
    return sampleCount.value;
  }
  
  // Set the geometry for the selected region
  sampler.value.setGeometry(selectedRegion.value.geometry);
  
  // Get the actual sample count that will be used
  const spec = sampler.value.getSamplingSpecificationFromSampleCount(sampleCount.value);
  return spec.count;
});

const sampleCountAdjustmentInfo = computed(() => {
  if (!selectedRegion.value || !sampler.value) {
    return null;
  }
  
  const actual = actualSampleCount.value;
  const requested = sampleCount.value;
  
  if (actual === requested) {
    return null; // No adjustment needed
  }
  
  // Get grid dimensions to explain the adjustment
  sampler.value.setGeometry(selectedRegion.value.geometry);
  const spec = sampler.value.getSamplingSpecificationFromSampleCount(requested);
  
  if (actual < requested) {
    return {
      type: 'reduced' as const,
      message: `Grid constraint: ${spec.lons} × ${spec.lats} = ${actual} samples`,
      reason: 'TEMPO grid spacing cannot accommodate the requested sample count'
    };
  } else {
    return {
      type: 'increased' as const,
      message: `Grid adjusted: ${spec.lons} × ${spec.lats} = ${actual} samples`,
      reason: 'Rounded up to fit TEMPO grid spacing'
    };
  }
});

// Computed
const canFetch = computed(() => {
  return selectedRegion.value !== null && selectedTimeRange.value !== null;
});

// Provide a safe current date for DateTimeRangeSelection
// Use the local singleDateSelected from useUniqueTimeSelection
const currentDate = computed(() => {
  const date = singleDateSelected.value;
  // Check if it's a valid Date object
  if (date && date instanceof Date && !isNaN(date.getTime())) {
    return date;
  }
  // Fallback to current date
  return new Date();
});

// Computed properties for Plotly graph
const plotlyDatasets = computed<PlotltGraphDataSet[]>(() => {
  const result: PlotltGraphDataSet[] = [];
  
  // Convert user datasets to plotly format
  datasets.value.forEach(dataset => {
    result.push(userDatasetToPlotly(dataset));
  });
  
  // Add failed request indicators
  const failedStats = requestStats.value.filter(stat => stat.status === 'error');
  console.log(`plotlyDatasets: Found ${failedStats.length} failed requests out of ${requestStats.value.length} total`);
  
  if (failedStats.length > 0) {
    // Calculate mean value from successful data points for y-position
    const allValues: number[] = [];
    datasets.value.forEach(dataset => {
      Object.values(dataset.samples || {}).forEach(sample => {
        if (sample.value !== null && !isNaN(sample.value)) {
          allValues.push(sample.value);
        }
      });
    });
    const meanValue = nanmean(allValues) ?? 1e16;
    console.log(`plotlyDatasets: Using mean value ${meanValue} for failed request lines, based on ${allValues.length} data points`);
    
    // Create red line segments for each failed time range
    failedStats.forEach((stat, idx) => {
      if (stat.timeRange) {
        const line = {
          x: [new Date(stat.timeRange.start), new Date(stat.timeRange.end)],
          y: [meanValue, meanValue],
          name: `Failed Request ${idx + 1}`,
          datasetOptions: {
            mode: 'lines',
            line: {
              color: 'red',
              width: 3,
              dash: 'solid'
            },
            showlegend: false,
            hovertemplate: `Failed Request<br>Time: %{x}<br>Error: ${stat.errorMessage || stat.errorType || 'Unknown'}<extra></extra>`
          }
        };
        result.push(line);

      }
    });
  }
  
  console.log(`plotlyDatasets: Returning ${result.length} total datasets (${result.length - datasets.value.length} failed request lines)`);
  return result;
});

const plotlyColors = computed(() => {
  return datasets.value.map(d => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (d as any).region?.color || 'blue';
  });
});

// Methods
function onMapReady(mapInstance: Map) {
  map.value = mapInstance;
  console.log('Map ready');
  
  // Initialize the sampler with service metadata
  const service = store.getTempoDataService('no2' as MoleculeType);
  service.withMetadataCache().then(meta => {
    sampler.value = new EsriSampler(meta);
    console.log('EsriSampler initialized with metadata');
  });
  
  // Initialize sample markers for each region (expected locations)
  predefinedRegions.value.forEach(region => {
    sampleMarkers[region.id] = useMultiMarker(map as Ref<Map | null>, {
      label: `samples-${region.id}`,
      color: region.color,
      fillColor: region.color,
      fillOpacity: 0.6,
      opacity: 1,
      radius: 4,
      outlineColor: '#ffffff',
      scale: 'screen'
    });
    
    // Initialize actual sample markers (red)
    actualSampleMarkers[region.id] = useMultiMarker(map as Ref<Map | null>, {
      label: `actual-samples-${region.id}`,
      color: '#ff0000',
      fillColor: '#ff0000',
      fillOpacity: 0.8,
      opacity: 1,
      radius: 3,
      outlineColor: '#ffffff',
      scale: 'screen'
    });
  });
  
  // Draw all predefined regions on the map
  drawRegionsOnMap();
}

function drawRegionsOnMap() {
  if (!map.value) return;
  
  predefinedRegions.value.forEach(region => {
    // Add region as a rectangle on the map
    if (map.value?.getSource(`region-${region.id}`)) {
      return; // Already added
    }
    
    map.value?.addSource(`region-${region.id}`, {
      type: 'geojson',
      data: {
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [[
            [region.geometry.xmin, region.geometry.ymin],
            [region.geometry.xmax, region.geometry.ymin],
            [region.geometry.xmax, region.geometry.ymax],
            [region.geometry.xmin, region.geometry.ymax],
            [region.geometry.xmin, region.geometry.ymin]
          ]]
        },
        properties: {}
      }
    });
    
    map.value?.addLayer({
      id: `region-${region.id}-fill`,
      type: 'fill',
      source: `region-${region.id}`,
      paint: {
        'fill-color': region.color,
        'fill-opacity': 0.2
      }
    });
    
    map.value?.addLayer({
      id: `region-${region.id}-outline`,
      type: 'line',
      source: `region-${region.id}`,
      paint: {
        'line-color': region.color,
        'line-width': 2
      }
    });
  });
}

function selectRegion(region: TestRegion) {
  selectedRegion.value = region;
  
  // Update map opacity to highlight selected region
  if (map.value) {
    predefinedRegions.value.forEach(r => {
      const opacity = r.id === region.id ? 0.4 : 0.1;
      const lineWidth = r.id === region.id ? 3 : 2;
      map.value?.setPaintProperty(`region-${r.id}-fill`, 'fill-opacity', opacity);
      map.value?.setPaintProperty(`region-${r.id}-outline`, 'line-width', lineWidth);
    });
    
    // Fly to the selected region
    const centerLng = (region.geometry.xmin + region.geometry.xmax) / 2;
    const centerLat = (region.geometry.ymin + region.geometry.ymax) / 2;
    map.value.flyTo({
      center: [centerLng, centerLat],
      zoom: region.id === 'large' ? 7 : region.id === 'medium' ? 9 : 11,
      duration: 1000
    });
  }
  
  // Hide sample points from all other regions
  predefinedRegions.value.forEach(r => {
    if (r.id !== region.id && showSamplePoints.value[r.id]) {
      showSamplePoints.value[r.id] = false;
      const marker = sampleMarkers[r.id];
      if (marker) {
        marker.clearMarkers();
      }
    }
  });
  
  // Automatically show expected sample points for this region
  showSamplePoints.value[region.id] = true;
  toggleSamplePoints(region);
}

function selectTimeRange(preset: PresetTimeRange) {
  selectedTimeRange.value = preset;
}

function handleCustomTimeRangeChange(
  ranges: MillisecondRange[],
  selectionType: string,
  customName: string,
  config: TimeRangeConfig
) {
  // Create a new custom time range from the selection
  const customRange: PresetTimeRange = {
    id: `custom-${Date.now()}`,
    name: customName || `Custom Range ${customTimeRanges.value.length + 1}`,
    description: selectionType === 'single' 
      ? 'Single date selection' 
      : `Pattern: ${ranges.length} time range(s)`,
    ranges: ranges,
    config: config
  };
  
  // Add to custom ranges
  customTimeRanges.value.push(customRange);
  
  // Automatically select this new range
  selectedTimeRange.value = customRange;
  
  // Switch to presets tab to show it's selected
  timeRangeTab.value = 'presets';
  
  console.log(`Custom time range created: ${customRange.name} with ${ranges.length} ranges`);
}

function removeCustomTimeRange(id: string) {
  const index = customTimeRanges.value.findIndex(r => r.id === id);
  if (index >= 0) {
    customTimeRanges.value.splice(index, 1);
    // If this was selected, clear selection
    if (selectedTimeRange.value?.id === id) {
      selectedTimeRange.value = null;
    }
  }
}

function toggleSamplePoints(region: TestRegion) {
  if (!map.value) return;
  
  const show = showSamplePoints.value[region.id];
  const marker = sampleMarkers[region.id];
  
  if (!marker) {
    console.warn(`No marker initialized for region ${region.id}`);
    return;
  }
  
  if (show) {
    // Always regenerate sample points to reflect current sample count
    if (!sampler.value) {
      console.warn('Sampler not initialized yet. Please wait for metadata to load.');
      showSamplePoints.value[region.id] = false; // Reset checkbox
      return;
    }
    
    // Set the geometry for this region
    sampler.value.setGeometry(region.geometry);
    
    // Get sample locations based on the current sample count setting
    const locations = sampler.value.getSampleLocationsGrid(sampleCount.value);
    
    // Update the stored locations
    regionSampleLocations.value[region.id] = locations;
    
    marker.addMarkers(locations);
    console.log(`Showing ${locations.length} expected sample points for ${region.name} (sample count: ${sampleCount.value})`);
  } else {
    // Hide sample points
    marker.clearMarkers();
    console.log(`Hiding expected sample points for ${region.name}`);
  }
}

function toggleActualSamplePoints(region: TestRegion) {
  if (!map.value) return;
  
  const show = showActualSamplePoints.value[region.id];
  const marker = actualSampleMarkers[region.id];
  
  if (!marker) {
    console.warn(`No actual marker initialized for region ${region.id}`);
    return;
  }
  
  if (show) {
    // Get actual sample locations from fetch results
    const locations = regionActualSampleLocations.value[region.id];
    
    if (!locations || locations.length === 0) {
      console.warn(`No actual sample locations available for ${region.name}. Fetch data first.`);
      showActualSamplePoints.value[region.id] = false; // Reset checkbox
      return;
    }
    
    marker.addMarkers(locations);
    console.log(`Showing ${locations.length} actual sample points (red) for ${region.name}`);
  } else {
    // Hide actual sample points
    marker.clearMarkers();
    console.log(`Hiding actual sample points for ${region.name}`);
  }
}

async function fetchData() {
  if (!canFetch.value || !selectedRegion.value || !selectedTimeRange.value) return;
  
  fetching.value = true;
  
  // Clear previous stats before starting new fetch
  requestStats.value = [];
  
  // Reset progress tracking
  completedRequests.value = 0;
  
  // Track overall timing
  const overallStartTime = performance.now();
  
  try {
    const service = store.getTempoDataService('no2' as MoleculeType);
    await service.withMetadataCache();
    
    // Set rate limit and retry limit from UI controls
    service.setRateLimit(rateLimitMs.value);
    service.setRetryLimit(maxRetries503.value);
    
    // Configure parceling settings
    service.setParcelingMode(parcelingMode.value);
    service.setDefaultParcelSize(defaultParcelSizeDays.value * 24 * 60 * 60 * 1000); // Convert days to ms
    service.setMaxSamplesPerRequest(maxSamplesPerRequest.value);
    service.setSafetyMargin(safetyMargin.value);
    
    // Pass in the available timestamps for smart parceling
    service.setAvailableTimestamps(esriTimesteps.value);
    
    // Use the selected time range (either preset or custom)
    // The ranges are already chunked via the parcel option in generateTimeRanges
    const timeRanges: { start: number; end: number }[] = selectedTimeRange.value.ranges;
    
    // Set total requests for progress tracking
    totalRequests.value = timeRanges.length;
    
    // Log sample count information
    const adjustmentInfo = sampleCountAdjustmentInfo.value;
    if (adjustmentInfo) {
      console.log(`%c⚠️  Sample Count Adjusted`, 'color: #FF9800; font-weight: bold; font-size: 12px');
      console.log(`   Requested: ${sampleCount.value}`);
      console.log(`   Actual:    ${actualSampleCount.value}`);
      console.log(`   Reason:    ${adjustmentInfo.reason}`);
      console.log(`   Grid:      ${adjustmentInfo.message}`);
    } else {
      console.log(`Using sample count: ${sampleCount.value}`);
    }
    
    console.log(`Fetching data for ${timeRanges.length} time chunks`);
    
    // Create progress callback
    const onProgress = (stats: RequestStats, completed: number, _total: number) => {
      // Add stats in real-time
      requestStats.value.push(stats);
      completedRequests.value = completed;
      // console.log(`Progress: ${completed}/${total} - ${stats.status === 'success' ? 'Success' : 'Error'}`);
    };
    
    // Fetch timeseries data with progress callback
    const result = await service.fetchTimeseriesData(
      selectedRegion.value.geometry,
      timeRanges,
      { 
        sampleCount: sampleCount.value,
        onProgress: onProgress
      }
    );
    
    // Stats are already added via the progress callback, no need to add them again
    console.log(`Fetch complete. Total stats collected: ${requestStats.value.length}`);
    
    // Store actual sample locations for this region (from fetched data)
    if (result.locations && result.locations.length > 0) {
      regionActualSampleLocations.value[selectedRegion.value.id] = result.locations;
      console.log(`Stored ${result.locations.length} actual sample locations for ${selectedRegion.value.name}`);
      
      // If actual sample points are currently showing for this region, update them
      if (showActualSamplePoints.value[selectedRegion.value.id]) {
        const marker = actualSampleMarkers[selectedRegion.value.id];
        if (marker) {
          marker.clearMarkers();
          marker.addMarkers(result.locations);
          console.log(`Updated displayed actual sample points (red) with fetched data locations`);
        }
      }
    }
    
    // Create dataset for display
    const dataset: UserDataset = {
      id: v4(),
      name: `${selectedRegion.value.name} - ${selectedTimeRange.value.name}`,
      samples: result.values,
      errors: result.errors,
      timeRange: {
        id: v4(),
        name: selectedTimeRange.value.name,
        description: selectedTimeRange.value.description,
        range: timeRanges.length === 1 ? timeRanges[0] : timeRanges,
        type: 'single'
      },
      region: {
        ...selectedRegion.value.geometry,
        id: selectedRegion.value.id,
        name: selectedRegion.value.name,
        color: selectedRegion.value.color
      },
      molecule: 'NO₂',
      locations: result.locations
    };
    
    datasets.value.push(dataset);
    
    console.log(`Fetched ${Object.keys(result.values).length} data points`);
    
    // ============================================================================
    // LOAD TEST SUMMARY - Comprehensive Statistics
    // ============================================================================
    const overallEndTime = performance.now();
    const totalDuration = overallEndTime - overallStartTime;
    
    // Calculate statistics from requestStats
    const allStats = requestStats.value;
    const successfulRequests = allStats.filter(s => s.status === 'success');
    const failedRequests = allStats.filter(s => s.status === 'error');
    const error503Count = allStats.filter(s => s.errorType === '503').length;
    const error400Count = allStats.filter(s => s.errorType === '400').length;
    const errorOtherCount = allStats.filter(s => s.errorType === 'other' && s.status === 'error').length;
    
    // Break down retried requests by success/failure
    const retriedRequests = allStats.filter(s => s.retried);
    const retriedSuccessful = retriedRequests.filter(s => s.status === 'success').length;
    const retriedFailed = retriedRequests.filter(s => s.status === 'error').length;
    const retriedFrom503Successful = allStats.filter(s => s.retriedFrom503 && s.status === 'success').length;
    const retriedFrom503Failed = allStats.filter(s => s.retriedFrom503 && s.status === 'error').length;
    
    // Timing statistics
    const durations = allStats.map(s => s.duration);
    const totalRequestTime = durations.reduce((sum, d) => sum + d, 0);
    const avgDuration = durations.length > 0 ? totalRequestTime / durations.length : 0;
    const minDuration = durations.length > 0 ? Math.min(...durations) : 0;
    const maxDuration = durations.length > 0 ? Math.max(...durations) : 0;
    
    // Sample statistics
    const totalSamplesFetched = successfulRequests.reduce((sum, s) => sum + s.sampleCount, 0);
    const expectedSamplesPerRequest = sampleCount.value;
    const actualTimeRangesFetched = allStats.length;
    const inputTimeRanges = timeRanges.length;
    
    // Data points - actual results returned
    const dataPointsReturned = Object.keys(result.values).length; // Unique timestamps in returned data
    const locationsCount = result.locations?.length || 0;
    
    // Print styled summary
    console.log('%c╔════════════════════════════════════════════════════════════════╗', 'color: #4CAF50; font-weight: bold');
    console.log('%c║                    LOAD TEST SUMMARY                          ║', 'color: #4CAF50; font-weight: bold');
    console.log('%c╚════════════════════════════════════════════════════════════════╝', 'color: #4CAF50; font-weight: bold');
    
    console.log('%c\n⏱️  TIMING', 'color: #2196F3; font-weight: bold; font-size: 14px');
    console.log(`   Total Duration:        ${(totalDuration / 1000).toFixed(2)}s`);
    console.log(`   Request Time (sum):    ${(totalRequestTime / 1000).toFixed(2)}s`);
    console.log(`   Avg Request Duration:  ${avgDuration.toFixed(0)}ms`);
    console.log(`   Min Request Duration:  ${minDuration.toFixed(0)}ms`);
    console.log(`   Max Request Duration:  ${maxDuration.toFixed(0)}ms`);
    
    console.log('%c\n📊 REQUEST STATISTICS', 'color: #FF9800; font-weight: bold; font-size: 14px');
    console.log(`   Input Time Ranges:     ${inputTimeRanges}`);
    console.log(`   Actual Requests Made:  ${actualTimeRangesFetched}`);
    console.log(`   Successful Requests:   ${successfulRequests.length} (${((successfulRequests.length / actualTimeRangesFetched) * 100).toFixed(1)}%)`);
    console.log(`   Failed Requests:       ${failedRequests.length} (${((failedRequests.length / actualTimeRangesFetched) * 100).toFixed(1)}%)`);
    
    if (retriedRequests.length > 0) {
      console.log(`   Retried Requests:      ${retriedRequests.length} total`);
      console.log(`     → Succeeded:         ${retriedSuccessful}`);
      console.log(`     → Failed:            ${retriedFailed}`);
      if (retriedFrom503Successful > 0 || retriedFrom503Failed > 0) {
        console.log(`   Retried from 503:      ${retriedFrom503Successful + retriedFrom503Failed} total`);
        console.log(`     → Succeeded:         ${retriedFrom503Successful}`);
        console.log(`     → Failed:            ${retriedFrom503Failed}`);
      }
    }
    
    if (failedRequests.length > 0) {
      console.log('%c\n⚠️  ERROR BREAKDOWN', 'color: #F44336; font-weight: bold; font-size: 14px');
      console.log(`   503 Errors:            ${error503Count}`);
      console.log(`   400 Errors:            ${error400Count}`);
      console.log(`   Other Errors:          ${errorOtherCount}`);
    }
    
    console.log('%c\n📈 SAMPLE STATISTICS', 'color: #9C27B0; font-weight: bold; font-size: 14px');
    console.log(`   Expected Samples/Time: ${expectedSamplesPerRequest}`);
    if (parcelingMode.value === 'smart' && result.expectedTotalSamples !== undefined) {
      // Use the actual expected total from the service (counts timestamps in time ranges)
      console.log(`   Expected Total Count:  ${result.expectedTotalSamples.toLocaleString()}`);
    }
    console.log(`   Total Samples Fetched: ${totalSamplesFetched.toLocaleString()}`);
    console.log(`   Avg Samples/Request:   ${actualTimeRangesFetched > 0 ? (totalSamplesFetched / actualTimeRangesFetched).toFixed(0) : 0}`);
    
    console.log('%c\n🎯 DATA RESULTS', 'color: #00BCD4; font-weight: bold; font-size: 14px');
    console.log(`   Data Points Returned:  ${dataPointsReturned} (unique timestamps)`);
    console.log(`   Unique Locations:      ${locationsCount}`);
    console.log(`   Total Data Values:     ${(dataPointsReturned * locationsCount).toLocaleString()}`);
    
    // Use expected total from service if available (smart mode)
    if (result.expectedTotalSamples !== undefined) {
      console.log(`   Expected Samples:      ${result.expectedTotalSamples.toLocaleString()}`);
      console.log(`   Actual Samples:        ${totalSamplesFetched.toLocaleString()}`);
      console.log(`   Sample Efficiency:     ${((totalSamplesFetched / result.expectedTotalSamples) * 100).toFixed(1)}%`);
    } else {
      // For non-smart modes, we don't have an accurate prediction
      console.log(`   Actual Samples:        ${totalSamplesFetched.toLocaleString()}`);
    }
    
    console.log('%c\n⚙️  CONFIGURATION', 'color: #607D8B; font-weight: bold; font-size: 14px');
    console.log(`   Parceling Mode:        ${parcelingMode.value}`);
    console.log(`   Sample Count:          ${sampleCount.value}`);
    console.log(`   Rate Limit:            ${rateLimitMs.value}ms`);
    console.log(`   Max Retries:           ${maxRetries503.value}`);
    if (parcelingMode.value === 'smart') {
      console.log(`   Max Samples/Request:   ${maxSamplesPerRequest.value}`);
      console.log(`   Safety Margin:         ${(safetyMargin.value * 100).toFixed(0)}%`);
    } else if (parcelingMode.value === 'default') {
      console.log(`   Parcel Size:           ${defaultParcelSizeDays.value} days`);
    }
    
    console.log('%c\n' + '═'.repeat(64), 'color: #4CAF50');
    
    // Add a fake failed request for testing visualization
    if (timeRanges.length > 0 && requestStats.value.length > 0) {
      // Pick a time range in the middle of the data
      const middleIndex = Math.floor(timeRanges.length / 2);
      const fakeFailedRange = timeRanges[middleIndex];
      
      const _fakeFailedStat: RequestStats = {
        status: 'error',
        statusCode: 400,
        errorType: '400',
        errorMessage: 'FAKE ERROR FOR TESTING: This is a simulated failed request to test visualization',
        duration: 1500,
        sampleCount: sampleCount.value,
        retried: false,
        timestamp: fakeFailedRange.start,
        timeRange: fakeFailedRange,
        url: 'https://fake-test-url.example.com'
      };
      
      // requestStats.value.push(fakeFailedStat);
      console.log('Added fake failed request for testing visualization');
    }
  } catch (error) {
    console.error('Error fetching data:', error);
    alert('Error fetching data. Check console for details.');
  } finally {
    fetching.value = false;
  }
}

// Generated by Copilot - Dry run function to generate URLs without fetching
async function dryRunData() {
  if (!canFetch.value || !selectedRegion.value || !selectedTimeRange.value) return;
  
  fetching.value = true;
  
  try {
    const service = store.getTempoDataService('no2' as MoleculeType);
    await service.withMetadataCache();
    
    // Configure parceling settings
    service.setParcelingMode(parcelingMode.value);
    service.setDefaultParcelSize(defaultParcelSizeDays.value * 24 * 60 * 60 * 1000);
    service.setMaxSamplesPerRequest(maxSamplesPerRequest.value);
    service.setSafetyMargin(safetyMargin.value);
    service.setAvailableTimestamps(esriTimesteps.value);
    
    // Enable dry run mode
    service.setDryRun(true);
    
    const timeRanges: { start: number; end: number }[] = selectedTimeRange.value.ranges;
    
    console.log('%c╔════════════════════════════════════════════════════════════════╗', 'color: #FF9800; font-weight: bold');
    console.log('%c║                      DRY RUN MODE                              ║', 'color: #FF9800; font-weight: bold');
    console.log('%c╚════════════════════════════════════════════════════════════════╝', 'color: #FF9800; font-weight: bold');
    console.log(`\nGenerating URLs for ${timeRanges.length} time chunks...`);
    
    // "Fetch" with dry run enabled
    await service.fetchTimeseriesData(
      selectedRegion.value.geometry,
      timeRanges,
      {
        sampleCount: actualSampleCount.value,
        dryRun: true
      }
    );
    
    // Get the generated URLs
    const urls = service.getRequestUrls();
    
    console.log('%c\n📋 GENERATED URLs:', 'color: #FF9800; font-weight: bold; font-size: 14px');
    console.log(`   Total URLs: ${urls.length}`);
    console.log('\nAll Request URLs (JSON):');
    console.log({url: urls});
    
    console.log('%c\n✅ Dry run complete - no requests were made', 'color: #4CAF50; font-weight: bold');
    
    // Disable dry run mode
    service.setDryRun(false);
    
    // alert(`Dry run complete! Generated ${urls.length} URLs.\nCheck console for details.`);
  } catch (error) {
    console.error('Error in dry run:', error);
    alert('Error in dry run. Check console for details.');
  } finally {
    fetching.value = false;
  }
}

// Note: splitTimeRanges is no longer needed - parceling is handled by generateTimeRanges

function clearStatistics() {
  requestStats.value = [];
  datasets.value = [];
  totalRequests.value = 0;
  completedRequests.value = 0;
  selectedTimeRangeForMap.value = null;
}

function handleTimeRangeSelect(stat: RequestStats) {
  selectedTimeRangeForMap.value = stat;
  
  // Update the map timestamp to show the selected time
  if (stat.timeRange) {
    // Set the map time to the start of the selected time range
    mapTimestamp.value = stat.timeRange.start;
    console.log(`Selected time range: ${new Date(stat.timeRange.start).toISOString()} - ${new Date(stat.timeRange.end).toISOString()}`);
  }
}

function handleGraphClick(point: {x: Date | number | string | null, y: number, customdata: unknown}) {
  console.log("handleGraphClick called with:", point);
  // Update the map timestamp when a point on the graph is clicked
  let timestamp: number | null = null;
  
  if (point.x instanceof Date) {
    timestamp = point.x.getTime();
  } else if (typeof point.x === 'number') {
    timestamp = point.x;
  } else if (typeof point.x === 'string') {
    // Parse string date
    const parsed = new Date(point.x);
    if (!isNaN(parsed.getTime())) {
      timestamp = parsed.getTime();
    }
  }
  
  console.log("Processed timestamp:", timestamp, "from point.x type:", typeof point.x);
  if (timestamp) {
    mapTimestamp.value = timestamp;
    console.log(`Graph point clicked: ${new Date(timestamp).toISOString()}, value: ${point.y}`);
  } else {
    console.warn("Could not extract timestamp from point.x:", point.x);
  }
}

function jitterRegions() {
  // Add small random offset to each region's bounds
  // Jitter amount: ±0.001 to ±0.005 degrees (roughly 100-500 meters)
  const jitterAmount = 0.1;
  
  predefinedRegions.value.forEach(region => {
    const dx = (Math.random() - 0.5) * 2 * jitterAmount;
    const dy = (Math.random() - 0.5) * 2 * jitterAmount;
    
    region.geometry.xmin += dx;
    region.geometry.xmax += dx;
    region.geometry.ymin += dy;
    region.geometry.ymax += dy;
    
    console.log(`Jittered ${region.name} by (${dx.toFixed(5)}, ${dy.toFixed(5)})`);
  });
  
  // Clear expected sample locations since regions have moved
  regionSampleLocations.value = {};
  // Note: Keep actual sample locations - they're from real data
  
  // Update map visualization
  if (map.value) {
    predefinedRegions.value.forEach(region => {
      const source = map.value?.getSource(`region-${region.id}`);
      if (source && 'setData' in source && typeof source.setData === 'function') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (source as any).setData({
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [[
              [region.geometry.xmin, region.geometry.ymin],
              [region.geometry.xmax, region.geometry.ymin],
              [region.geometry.xmax, region.geometry.ymax],
              [region.geometry.xmin, region.geometry.ymax],
              [region.geometry.xmin, region.geometry.ymin]
            ]]
          },
          properties: {}
        });
      }
    });
  }
  
  console.log('Regions jittered for cache busting');
}

// Watch for parcel size changes and update selected preset if needed
watch(defaultParcelSizeDays, () => {
  // If a preset time range is selected, update it with the new parcel size
  if (selectedTimeRange.value) {
    const presetId = selectedTimeRange.value.id;
    
    // Check if this is a preset (not a custom range)
    const updatedPreset = presetTimeRanges.value.find(p => p.id === presetId);
    
    if (updatedPreset) {
      // Update the selected range with the newly generated ranges
      selectedTimeRange.value = updatedPreset;
      console.log(`Updated selected time range "${updatedPreset.name}" with ${updatedPreset.ranges.length} ranges for parcel size: ${defaultParcelSizeDays.value} days`);
    }
  }
});

// Watch for sample count changes and update displayed sample points automatically
watch(sampleCount, () => {
  // Update all visible sample point displays
  predefinedRegions.value.forEach(region => {
    if (showSamplePoints.value[region.id]) {
      // Regenerate and update the displayed points
      if (sampler.value) {
        sampler.value.setGeometry(region.geometry);
        const locations = sampler.value.getSampleLocationsGrid(sampleCount.value);
        regionSampleLocations.value[region.id] = locations;
        
        const marker = sampleMarkers[region.id];
        if (marker) {
          marker.clearMarkers();
          marker.addMarkers(locations);
          console.log(`Updated sample points for ${region.name}: ${locations.length} locations (sample count: ${sampleCount.value})`);
        }
      }
    }
  });
});

// Note: Timestamps are now fetched directly via useEsriTimesteps composable
// No need for onMounted initialization

return (_ctx: any,_cache: any) => {
  const _component_v_app_bar_title = _resolveComponent("v-app-bar-title")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_app_bar = _resolveComponent("v-app-bar")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_alert = _resolveComponent("v-alert")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_chip_group = _resolveComponent("v-chip-group")!
  const _component_v_divider = _resolveComponent("v-divider")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_checkbox = _resolveComponent("v-checkbox")!
  const _component_v_list_item_title = _resolveComponent("v-list-item-title")!
  const _component_v_list_item = _resolveComponent("v-list-item")!
  const _component_v_list = _resolveComponent("v-list")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_tab = _resolveComponent("v-tab")!
  const _component_v_tabs = _resolveComponent("v-tabs")!
  const _component_v_table = _resolveComponent("v-table")!
  const _component_v_window_item = _resolveComponent("v-window-item")!
  const _component_v_window = _resolveComponent("v-window")!
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_expand_transition = _resolveComponent("v-expand-transition")!
  const _component_v_progress_linear = _resolveComponent("v-progress-linear")!
  const _component_v_container = _resolveComponent("v-container")!
  const _component_v_main = _resolveComponent("v-main")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_dialog = _resolveComponent("v-dialog")!
  const _component_v_app = _resolveComponent("v-app")!

  return (_openBlock(), _createBlock(_component_v_app, null, {
    default: _withCtx(() => [
      _createVNode(_component_v_app_bar, {
        color: "primary",
        density: "compact"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_app_bar_title, null, {
            default: _withCtx(() => [...(_cache[12] || (_cache[12] = [
              _createTextVNode("TEMPO Data Service Testing Interface", -1)
            ]))]),
            _: 1
          }),
          _createVNode(_component_v_spacer),
          _createVNode(_component_v_btn, {
            icon: "mdi-help-circle",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (showHelp.value = true))
          })
        ]),
        _: 1
      }),
      _createVNode(_component_v_main, null, {
        default: _withCtx(() => [
          _createVNode(_component_v_container, {
            fluid: "",
            class: "pa-4"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_row, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_col, {
                    cols: "12",
                    lg: "7"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_card, { class: "mb-4" }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_card_title, { class: "d-flex align-center" }, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_chip, {
                                color: "primary",
                                variant: "flat",
                                size: "small",
                                class: "mr-2"
                              }, {
                                default: _withCtx(() => [...(_cache[13] || (_cache[13] = [
                                  _createTextVNode("STEP 1", -1)
                                ]))]),
                                _: 1
                              }),
                              _cache[15] || (_cache[15] = _createTextVNode(" Select Test Region ", -1)),
                              _createVNode(_component_v_spacer),
                              _createVNode(InfoButton, { "tooltip-text": "Learn about test regions" }, {
                                default: _withCtx(() => [...(_cache[14] || (_cache[14] = [
                                  _createElementVNode("p", null, [
                                    _createElementVNode("strong", null, "Test Regions")
                                  ], -1),
                                  _createElementVNode("p", null, "Select a geographic area to analyze. Region size affects data volume:", -1),
                                  _createElementVNode("ul", null, [
                                    _createElementVNode("li", null, [
                                      _createElementVNode("strong", null, "Small:"),
                                      _createTextVNode(" Fast testing, minimal data")
                                    ]),
                                    _createElementVNode("li", null, [
                                      _createElementVNode("strong", null, "Medium:"),
                                      _createTextVNode(" Balanced data volume")
                                    ]),
                                    _createElementVNode("li", null, [
                                      _createElementVNode("strong", null, "Large:"),
                                      _createTextVNode(" Performance stress testing")
                                    ])
                                  ], -1),
                                  _createElementVNode("p", null, [
                                    _createElementVNode("strong", null, "Sample Count:"),
                                    _createTextVNode(" Number of sampling points across the region. More points = better spatial resolution but larger datasets.")
                                  ], -1)
                                ]))]),
                                _: 1
                              })
                            ]),
                            _: 1
                          }),
                          _createVNode(_component_v_card_text, null, {
                            default: _withCtx(() => [
                              (!selectedRegion.value)
                                ? (_openBlock(), _createBlock(_component_v_alert, {
                                    key: 0,
                                    type: "info",
                                    variant: "tonal",
                                    density: "compact",
                                    class: "mb-3",
                                    icon: "mdi-cursor-default-click"
                                  }, {
                                    default: _withCtx(() => [...(_cache[16] || (_cache[16] = [
                                      _createElementVNode("strong", null, "Select a region:", -1),
                                      _createTextVNode(" Click a chip below to choose your study area ", -1)
                                    ]))]),
                                    _: 1
                                  }))
                                : _createCommentVNode("", true),
                              (mapTimestamp.value)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                                    _cache[17] || (_cache[17] = _createElementVNode("strong", null, "Current Map Time (UTC):", -1)),
                                    _createElementVNode("span", _hoisted_2, _toDisplayString(new Date(mapTimestamp.value).toLocaleString('en-US', { timeZone: 'UTC', dateStyle: 'medium', timeStyle: 'medium' })), 1)
                                  ]))
                                : _createCommentVNode("", true),
                              _createVNode(_component_v_row, null, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_col, {
                                    cols: "12",
                                    md: "7"
                                  }, {
                                    default: _withCtx(() => [
                                      _createElementVNode("div", _hoisted_3, [
                                        _createVNode(EsriMap, {
                                          mapID: 'test-map',
                                          home: homeState.value,
                                          initial: homeState.value,
                                          "show-roads": true,
                                          molecule: molecule.value,
                                          timestamp: mapTimestamp.value,
                                          opacity: mapOpacity.value,
                                          "show-field-of-regard": false,
                                          onReady: onMapReady
                                        }, null, 8, ["home", "initial", "molecule", "timestamp", "opacity"])
                                      ])
                                    ]),
                                    _: 1
                                  }),
                                  _createVNode(_component_v_col, {
                                    cols: "12",
                                    md: "5"
                                  }, {
                                    default: _withCtx(() => [
                                      _createVNode(_component_v_card, {
                                        variant: "outlined",
                                        class: "pa-3"
                                      }, {
                                        default: _withCtx(() => [
                                          _cache[25] || (_cache[25] = _createElementVNode("div", { class: "d-flex align-center mb-2" }, [
                                            _createElementVNode("h4", null, "Available Regions")
                                          ], -1)),
                                          _createVNode(_component_v_chip_group, {
                                            column: "",
                                            mandatory: ""
                                          }, {
                                            default: _withCtx(() => [
                                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(predefinedRegions.value, (region) => {
                                                return (_openBlock(), _createBlock(_component_v_chip, {
                                                  key: region.id,
                                                  color: region.color,
                                                  variant: "outlined",
                                                  onClick: ($event: any) => (selectRegion(region)),
                                                  class: _normalizeClass({ 'v-chip--active': selectedRegion.value?.id === region.id })
                                                }, {
                                                  default: _withCtx(() => [
                                                    _createTextVNode(_toDisplayString(region.name) + " (" + _toDisplayString(region.size) + ") ", 1)
                                                  ]),
                                                  _: 2
                                                }, 1032, ["color", "onClick", "class"]))
                                              }), 128))
                                            ]),
                                            _: 1
                                          }),
                                          (selectedRegion.value)
                                            ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                                                _cache[18] || (_cache[18] = _createElementVNode("strong", null, "Selected:", -1)),
                                                _createTextVNode(" " + _toDisplayString(selectedRegion.value.name) + " ", 1),
                                                _cache[19] || (_cache[19] = _createElementVNode("br", null, null, -1)),
                                                _createTextVNode(" Bounds: " + _toDisplayString(selectedRegion.value.geometry.xmin.toFixed(3)) + ", " + _toDisplayString(selectedRegion.value.geometry.ymin.toFixed(3)) + " to " + _toDisplayString(selectedRegion.value.geometry.xmax.toFixed(3)) + ", " + _toDisplayString(selectedRegion.value.geometry.ymax.toFixed(3)), 1)
                                              ]))
                                            : _createCommentVNode("", true),
                                          _createVNode(_component_v_divider, { class: "my-3" }),
                                          _cache[26] || (_cache[26] = _createElementVNode("h4", { class: "mb-2" }, "Spatial Sampling", -1)),
                                          _createVNode(_component_v_text_field, {
                                            modelValue: sampleCount.value,
                                            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((sampleCount).value = $event)),
                                            modelModifiers: { number: true },
                                            label: "Sample Count",
                                            type: "number",
                                            min: "1",
                                            max: "100",
                                            density: "compact",
                                            hint: "Sampling points across region (1-100). More points = better coverage.",
                                            "persistent-hint": ""
                                          }, null, 8, ["modelValue"]),
                                          (sampleCountAdjustmentInfo.value)
                                            ? (_openBlock(), _createBlock(_component_v_alert, {
                                                key: 1,
                                                type: sampleCountAdjustmentInfo.value.type === 'reduced' ? 'warning' : 'info',
                                                density: "compact",
                                                variant: "tonal",
                                                class: "mt-2"
                                              }, {
                                                default: _withCtx(() => [
                                                  _createElementVNode("div", _hoisted_5, [
                                                    _createElementVNode("strong", null, "Actual: " + _toDisplayString(actualSampleCount.value) + " locations", 1),
                                                    _cache[20] || (_cache[20] = _createElementVNode("br", null, null, -1)),
                                                    _createTextVNode(" " + _toDisplayString(sampleCountAdjustmentInfo.value.message), 1)
                                                  ])
                                                ]),
                                                _: 1
                                              }, 8, ["type"]))
                                            : _createCommentVNode("", true),
                                          _createVNode(_component_v_divider, { class: "my-3" }),
                                          _createVNode(_component_v_btn, {
                                            block: "",
                                            size: "small",
                                            variant: "outlined",
                                            color: "warning",
                                            onClick: jitterRegions
                                          }, {
                                            default: _withCtx(() => [
                                              _createVNode(_component_v_icon, { start: "" }, {
                                                default: _withCtx(() => [...(_cache[21] || (_cache[21] = [
                                                  _createTextVNode("mdi-shuffle-variant", -1)
                                                ]))]),
                                                _: 1
                                              }),
                                              _cache[22] || (_cache[22] = _createTextVNode(" Randomize Regions ", -1))
                                            ]),
                                            _: 1
                                          }),
                                          _cache[27] || (_cache[27] = _createElementVNode("div", { class: "text-caption mt-1 text-grey" }, " Slightly shifts region boundaries to bypass server caching ", -1)),
                                          _createVNode(_component_v_divider, { class: "my-3" }),
                                          _createElementVNode("details", null, [
                                            _cache[24] || (_cache[24] = _createElementVNode("summary", {
                                              class: "cursor-pointer text-body-2 font-weight-bold mb-2",
                                              style: {"cursor":"pointer"}
                                            }, " Display Sample Points ", -1)),
                                            _createElementVNode("div", _hoisted_6, [
                                              _cache[23] || (_cache[23] = _createElementVNode("div", { class: "text-caption font-weight-bold mb-1" }, "Actual Samples (Red):", -1)),
                                              _createVNode(_component_v_list, {
                                                density: "compact",
                                                class: "pa-0"
                                              }, {
                                                default: _withCtx(() => [
                                                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(predefinedRegions.value, (region) => {
                                                    return (_openBlock(), _createBlock(_component_v_list_item, {
                                                      key: `actual-samples-${region.id}`,
                                                      class: "px-0",
                                                      disabled: !regionActualSampleLocations.value[region.id] || regionActualSampleLocations.value[region.id].length === 0
                                                    }, {
                                                      prepend: _withCtx(() => [
                                                        _createVNode(_component_v_checkbox, {
                                                          modelValue: showActualSamplePoints.value[region.id],
                                                          "onUpdate:modelValue": ($event: any) => ((showActualSamplePoints.value[region.id]) = $event),
                                                          color: "red",
                                                          "hide-details": "",
                                                          density: "compact",
                                                          disabled: !regionActualSampleLocations.value[region.id] || regionActualSampleLocations.value[region.id].length === 0,
                                                          onChange: ($event: any) => (toggleActualSamplePoints(region))
                                                        }, null, 8, ["modelValue", "onUpdate:modelValue", "disabled", "onChange"])
                                                      ]),
                                                      default: _withCtx(() => [
                                                        _createVNode(_component_v_list_item_title, { class: "text-body-2" }, {
                                                          default: _withCtx(() => [
                                                            _createTextVNode(_toDisplayString(region.name) + " ", 1),
                                                            (regionActualSampleLocations.value[region.id])
                                                              ? (_openBlock(), _createElementBlock("span", _hoisted_7, " (" + _toDisplayString(regionActualSampleLocations.value[region.id].length) + " points) ", 1))
                                                              : _createCommentVNode("", true)
                                                          ]),
                                                          _: 2
                                                        }, 1024)
                                                      ]),
                                                      _: 2
                                                    }, 1032, ["disabled"]))
                                                  }), 128))
                                                ]),
                                                _: 1
                                              })
                                            ])
                                          ])
                                        ]),
                                        _: 1
                                      })
                                    ]),
                                    _: 1
                                  })
                                ]),
                                _: 1
                              })
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_v_card, { class: "mb-4" }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_card_title, { class: "d-flex align-center" }, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_chip, {
                                color: "primary",
                                variant: "flat",
                                size: "small",
                                class: "mr-2"
                              }, {
                                default: _withCtx(() => [...(_cache[28] || (_cache[28] = [
                                  _createTextVNode("STEP 2", -1)
                                ]))]),
                                _: 1
                              }),
                              _cache[30] || (_cache[30] = _createTextVNode(" Select Time Range ", -1)),
                              _createVNode(_component_v_spacer),
                              _createVNode(InfoButton, { "tooltip-text": "Learn about time ranges" }, {
                                default: _withCtx(() => [...(_cache[29] || (_cache[29] = [
                                  _createElementVNode("p", null, [
                                    _createElementVNode("strong", null, "Time Range Selection")
                                  ], -1),
                                  _createElementVNode("p", null, [
                                    _createElementVNode("strong", null, "Presets:"),
                                    _createTextVNode(" Ready-to-use time periods for common scenarios")
                                  ], -1),
                                  _createElementVNode("p", null, [
                                    _createElementVNode("strong", null, "Custom:"),
                                    _createTextVNode(" Build your own ranges with specific dates, times, or patterns")
                                  ], -1),
                                  _createElementVNode("p", null, [
                                    _createElementVNode("strong", null, "Tip:"),
                                    _createTextVNode(" More time ranges = more requests. Use \"Dry Run\" to preview first.")
                                  ], -1)
                                ]))]),
                                _: 1
                              })
                            ]),
                            _: 1
                          }),
                          _createVNode(_component_v_card_text, null, {
                            default: _withCtx(() => [
                              (!selectedTimeRange.value)
                                ? (_openBlock(), _createBlock(_component_v_alert, {
                                    key: 0,
                                    type: "info",
                                    variant: "tonal",
                                    density: "compact",
                                    class: "mb-3",
                                    icon: "mdi-calendar-clock"
                                  }, {
                                    default: _withCtx(() => [...(_cache[31] || (_cache[31] = [
                                      _createElementVNode("strong", null, "Choose a time period:", -1),
                                      _createTextVNode(" Select a preset or create your own ", -1)
                                    ]))]),
                                    _: 1
                                  }))
                                : _createCommentVNode("", true),
                              _createVNode(_component_v_row, null, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_col, {
                                    cols: "12",
                                    md: "6"
                                  }, {
                                    default: _withCtx(() => [
                                      _createVNode(_component_v_tabs, {
                                        modelValue: timeRangeTab.value,
                                        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((timeRangeTab).value = $event)),
                                        density: "compact"
                                      }, {
                                        default: _withCtx(() => [
                                          _createVNode(_component_v_tab, { value: "presets" }, {
                                            default: _withCtx(() => [...(_cache[32] || (_cache[32] = [
                                              _createTextVNode("Presets", -1)
                                            ]))]),
                                            _: 1
                                          }),
                                          _createVNode(_component_v_tab, { value: "custom" }, {
                                            default: _withCtx(() => [...(_cache[33] || (_cache[33] = [
                                              _createTextVNode("Custom", -1)
                                            ]))]),
                                            _: 1
                                          })
                                        ]),
                                        _: 1
                                      }, 8, ["modelValue"]),
                                      _createVNode(_component_v_window, {
                                        modelValue: timeRangeTab.value,
                                        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((timeRangeTab).value = $event)),
                                        class: "mt-3",
                                        eager: ""
                                      }, {
                                        default: _withCtx(() => [
                                          _createVNode(_component_v_window_item, {
                                            value: "presets",
                                            eager: ""
                                          }, {
                                            default: _withCtx(() => [
                                              _createVNode(_component_v_table, {
                                                density: "compact",
                                                class: "text-caption"
                                              }, {
                                                default: _withCtx(() => [
                                                  _cache[35] || (_cache[35] = _createElementVNode("thead", null, [
                                                    _createElementVNode("tr", null, [
                                                      _createElementVNode("th", { class: "text-left" }, "Preset"),
                                                      _createElementVNode("th", { class: "text-left" }, "Description"),
                                                      _createElementVNode("th", { class: "text-center" }, "# of Distinct Time Ranges")
                                                    ])
                                                  ], -1)),
                                                  _createElementVNode("tbody", null, [
                                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(allTimeRanges.value, (preset) => {
                                                      return (_openBlock(), _createElementBlock("tr", {
                                                        key: preset.id,
                                                        onClick: ($event: any) => (selectTimeRange(preset)),
                                                        class: _normalizeClass({ 'bg-primary-lighten-4': selectedTimeRange.value?.id === preset.id }),
                                                        style: {"cursor":"pointer"}
                                                      }, [
                                                        _createElementVNode("td", _hoisted_9, [
                                                          (selectedTimeRange.value?.id === preset.id)
                                                            ? (_openBlock(), _createBlock(_component_v_icon, {
                                                                key: 0,
                                                                size: "small",
                                                                color: "primary",
                                                                start: ""
                                                              }, {
                                                                default: _withCtx(() => [...(_cache[34] || (_cache[34] = [
                                                                  _createTextVNode(" mdi-check-circle ", -1)
                                                                ]))]),
                                                                _: 1
                                                              }))
                                                            : _createCommentVNode("", true),
                                                          _createTextVNode(" " + _toDisplayString(preset.name), 1)
                                                        ]),
                                                        _createElementVNode("td", null, _toDisplayString(preset.description), 1),
                                                        _createElementVNode("td", _hoisted_10, [
                                                          _createVNode(_component_v_chip, {
                                                            size: "x-small",
                                                            color: "info",
                                                            variant: "flat"
                                                          }, {
                                                            default: _withCtx(() => [
                                                              _createTextVNode(_toDisplayString(preset.ranges.length), 1)
                                                            ]),
                                                            _: 2
                                                          }, 1024)
                                                        ])
                                                      ], 10, _hoisted_8))
                                                    }), 128))
                                                  ])
                                                ]),
                                                _: 1
                                              })
                                            ]),
                                            _: 1
                                          }),
                                          _createVNode(_component_v_window_item, { value: "custom" }, {
                                            default: _withCtx(() => [
                                              (_openBlock(), _createBlock(DateTimeRangeSelection, {
                                                key: dateTimeKey.value,
                                                "current-date": currentDate.value,
                                                "allowed-dates": _unref(uniqueDays),
                                                onRangesChange: handleCustomTimeRangeChange
                                              }, null, 8, ["current-date", "allowed-dates"])),
                                              (customTimeRanges.value.length > 0)
                                                ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                                                    _createVNode(_component_v_divider, { class: "mb-2" }),
                                                    _cache[36] || (_cache[36] = _createElementVNode("div", { class: "text-caption font-weight-bold mb-2" }, "Custom Ranges:", -1)),
                                                    _createVNode(_component_v_chip_group, { column: "" }, {
                                                      default: _withCtx(() => [
                                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(customTimeRanges.value, (custom) => {
                                                          return (_openBlock(), _createBlock(_component_v_chip, {
                                                            key: custom.id,
                                                            variant: "outlined",
                                                            size: "small",
                                                            closable: "",
                                                            onClick: ($event: any) => (selectTimeRange(custom)),
                                                            "onClick:close": ($event: any) => (removeCustomTimeRange(custom.id)),
                                                            class: _normalizeClass({ 'v-chip--active': selectedTimeRange.value?.id === custom.id })
                                                          }, {
                                                            default: _withCtx(() => [
                                                              _createTextVNode(_toDisplayString(custom.name), 1)
                                                            ]),
                                                            _: 2
                                                          }, 1032, ["onClick", "onClick:close", "class"]))
                                                        }), 128))
                                                      ]),
                                                      _: 1
                                                    })
                                                  ]))
                                                : _createCommentVNode("", true)
                                            ]),
                                            _: 1
                                          })
                                        ]),
                                        _: 1
                                      }, 8, ["modelValue"])
                                    ]),
                                    _: 1
                                  }),
                                  _createVNode(_component_v_col, {
                                    cols: "12",
                                    md: "6"
                                  }, {
                                    default: _withCtx(() => [
                                      _createElementVNode("div", _hoisted_12, [
                                        _createVNode(_component_v_chip, {
                                          color: "primary",
                                          variant: "flat",
                                          size: "small",
                                          class: "mr-2"
                                        }, {
                                          default: _withCtx(() => [...(_cache[37] || (_cache[37] = [
                                            _createTextVNode("STEP 3", -1)
                                          ]))]),
                                          _: 1
                                        }),
                                        _cache[39] || (_cache[39] = _createElementVNode("h4", null, "Configure & Fetch", -1)),
                                        _createVNode(_component_v_spacer),
                                        _createVNode(InfoButton, { "tooltip-text": "Learn about fetch settings" }, {
                                          default: _withCtx(() => [...(_cache[38] || (_cache[38] = [
                                            _createElementVNode("p", null, [
                                              _createElementVNode("strong", null, "Parceling:"),
                                              _createTextVNode(" How to split time ranges into requests")
                                            ], -1),
                                            _createElementVNode("ul", null, [
                                              _createElementVNode("li", null, [
                                                _createElementVNode("strong", null, "None:"),
                                                _createTextVNode(" Single requests (fast but may fail on large ranges)")
                                              ]),
                                              _createElementVNode("li", null, [
                                                _createElementVNode("strong", null, "Default:"),
                                                _createTextVNode(" Fixed-size chunks (predictable, configurable)")
                                              ]),
                                              _createElementVNode("li", null, [
                                                _createElementVNode("strong", null, "Smart:"),
                                                _createTextVNode(" Optimized batches (most efficient, recommended)")
                                              ])
                                            ], -1),
                                            _createElementVNode("p", null, [
                                              _createElementVNode("strong", null, "Rate Limiting:"),
                                              _createTextVNode(" Delay between requests to prevent server overload.")
                                            ], -1)
                                          ]))]),
                                          _: 1
                                        })
                                      ]),
                                      _createVNode(_component_v_card, {
                                        variant: "outlined",
                                        class: "pa-2 mb-2"
                                      }, {
                                        default: _withCtx(() => [
                                          _cache[48] || (_cache[48] = _createElementVNode("div", { class: "text-caption font-weight-bold mb-2" }, "1. Parceling Strategy", -1)),
                                          _createVNode(_component_v_alert, {
                                            type: "info",
                                            variant: "tonal",
                                            density: "compact",
                                            class: "mb-2 text-caption"
                                          }, {
                                            default: _withCtx(() => [...(_cache[40] || (_cache[40] = [
                                              _createElementVNode("strong", null, "Purpose:", -1),
                                              _createTextVNode(" Break large time ranges into manageable requests ", -1)
                                            ]))]),
                                            _: 1
                                          }),
                                          _createVNode(_component_v_select, {
                                            modelValue: parcelingMode.value,
                                            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((parcelingMode).value = $event)),
                                            label: "Parceling Mode",
                                            items: parcelingModeOptions,
                                            density: "compact",
                                            class: "mb-2"
                                          }, null, 8, ["modelValue"]),
                                          (parcelingMode.value === 'none')
                                            ? (_openBlock(), _createBlock(_component_v_alert, {
                                                key: 0,
                                                type: "info",
                                                variant: "outlined",
                                                density: "compact",
                                                class: "mb-2 text-caption"
                                              }, {
                                                default: _withCtx(() => [...(_cache[41] || (_cache[41] = [
                                                  _createElementVNode("strong", null, "None (As-Is):", -1),
                                                  _createTextVNode(" Sends one request per time range without splitting. Fast for short ranges, but may fail if a single range exceeds server limits (typically ~2000 samples = locations × timestamps). ", -1)
                                                ]))]),
                                                _: 1
                                              }))
                                            : _createCommentVNode("", true),
                                          (parcelingMode.value === 'default')
                                            ? (_openBlock(), _createBlock(_component_v_alert, {
                                                key: 1,
                                                type: "info",
                                                variant: "outlined",
                                                density: "compact",
                                                class: "mb-2 text-caption"
                                              }, {
                                                default: _withCtx(() => [...(_cache[42] || (_cache[42] = [
                                                  _createElementVNode("strong", null, "Default (Fixed Chunks):", -1),
                                                  _createTextVNode(" Splits time ranges into fixed-size chunks based on days. Predictable and configurable. Good when you want consistent request sizes regardless of data density. ", -1)
                                                ]))]),
                                                _: 1
                                              }))
                                            : _createCommentVNode("", true),
                                          (parcelingMode.value === 'smart')
                                            ? (_openBlock(), _createBlock(_component_v_alert, {
                                                key: 2,
                                                type: "success",
                                                variant: "outlined",
                                                density: "compact",
                                                class: "mb-2 text-caption"
                                              }, {
                                                default: _withCtx(() => [...(_cache[43] || (_cache[43] = [
                                                  _createElementVNode("strong", null, "Smart (Recommended):", -1),
                                                  _createTextVNode(" Analyzes available timestamps and creates optimally-sized requests that maximize data per request while staying under server limits. Most efficient for long time ranges with sparse data. ", -1)
                                                ]))]),
                                                _: 1
                                              }))
                                            : _createCommentVNode("", true),
                                          _createVNode(_component_v_expand_transition, null, {
                                            default: _withCtx(() => [
                                              (parcelingMode.value === 'default')
                                                ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                                                    _cache[44] || (_cache[44] = _createElementVNode("div", { class: "text-caption text-medium-emphasis mb-2" }, " Each time range will be split into chunks of the specified number of days. ", -1)),
                                                    _createVNode(_component_v_text_field, {
                                                      modelValue: defaultParcelSizeDays.value,
                                                      "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((defaultParcelSizeDays).value = $event)),
                                                      modelModifiers: { number: true },
                                                      label: "Chunk Size (days)",
                                                      type: "number",
                                                      min: "1",
                                                      max: "365",
                                                      density: "compact",
                                                      hint: "Days per request. Smaller = more requests but safer.",
                                                      "persistent-hint": ""
                                                    }, null, 8, ["modelValue"]),
                                                    _cache[45] || (_cache[45] = _createElementVNode("div", { class: "text-caption text-medium-emphasis mt-1" }, [
                                                      _createElementVNode("strong", null, "Example:"),
                                                      _createTextVNode(" A 30-day range with 7-day chunks = ~5 requests ")
                                                    ], -1))
                                                  ]))
                                                : _createCommentVNode("", true)
                                            ]),
                                            _: 1
                                          }),
                                          _createVNode(_component_v_expand_transition, null, {
                                            default: _withCtx(() => [
                                              (parcelingMode.value === 'smart')
                                                ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                                                    _cache[46] || (_cache[46] = _createElementVNode("div", { class: "text-caption text-medium-emphasis mb-2" }, " Smart mode groups timestamps to maximize efficiency while respecting server limits. ", -1)),
                                                    _createVNode(_component_v_row, { dense: "" }, {
                                                      default: _withCtx(() => [
                                                        _createVNode(_component_v_col, { cols: "6" }, {
                                                          default: _withCtx(() => [
                                                            _createVNode(_component_v_text_field, {
                                                              modelValue: maxSamplesPerRequest.value,
                                                              "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((maxSamplesPerRequest).value = $event)),
                                                              modelModifiers: { number: true },
                                                              label: "Max Samples/Request",
                                                              type: "number",
                                                              min: "100",
                                                              max: "10000",
                                                              density: "compact",
                                                              hint: "Server limit (default: 2000)",
                                                              "persistent-hint": ""
                                                            }, null, 8, ["modelValue"])
                                                          ]),
                                                          _: 1
                                                        }),
                                                        _createVNode(_component_v_col, { cols: "6" }, {
                                                          default: _withCtx(() => [
                                                            _createVNode(_component_v_text_field, {
                                                              modelValue: safetyMargin.value,
                                                              "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((safetyMargin).value = $event)),
                                                              modelModifiers: { number: true },
                                                              label: "Safety Margin",
                                                              type: "number",
                                                              min: "0.1",
                                                              max: "1.0",
                                                              step: "0.05",
                                                              density: "compact",
                                                              hint: "Use % of max (0.9 = 90%)",
                                                              "persistent-hint": ""
                                                            }, null, 8, ["modelValue"])
                                                          ]),
                                                          _: 1
                                                        })
                                                      ]),
                                                      _: 1
                                                    }),
                                                    _cache[47] || (_cache[47] = _createElementVNode("div", { class: "text-caption text-medium-emphasis mt-2" }, [
                                                      _createElementVNode("strong", null, "How it works:"),
                                                      _createTextVNode(" Calculates samples/request = locations × timestamps. Groups consecutive timestamps into batches that stay under (Max × Safety Margin). Minimizes total requests. ")
                                                    ], -1))
                                                  ]))
                                                : _createCommentVNode("", true)
                                            ]),
                                            _: 1
                                          })
                                        ]),
                                        _: 1
                                      }),
                                      _createVNode(_component_v_card, {
                                        variant: "outlined",
                                        class: "pa-2 mb-2"
                                      }, {
                                        default: _withCtx(() => [
                                          _cache[50] || (_cache[50] = _createElementVNode("div", { class: "text-caption font-weight-bold mb-2" }, "2. Rate Limiting & Retries", -1)),
                                          _createVNode(_component_v_alert, {
                                            type: "info",
                                            variant: "tonal",
                                            density: "compact",
                                            class: "mb-2 text-caption"
                                          }, {
                                            default: _withCtx(() => [...(_cache[49] || (_cache[49] = [
                                              _createElementVNode("strong", null, "Purpose:", -1),
                                              _createTextVNode(" Pace requests to avoid overwhelming the server ", -1)
                                            ]))]),
                                            _: 1
                                          }),
                                          _cache[51] || (_cache[51] = _createElementVNode("div", { class: "text-caption text-medium-emphasis mb-2" }, " Controls the delay between consecutive requests and how many times to retry on server errors. ", -1)),
                                          _createVNode(_component_v_row, { dense: "" }, {
                                            default: _withCtx(() => [
                                              _createVNode(_component_v_col, { cols: "6" }, {
                                                default: _withCtx(() => [
                                                  _createVNode(_component_v_text_field, {
                                                    modelValue: rateLimitMs.value,
                                                    "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((rateLimitMs).value = $event)),
                                                    modelModifiers: { number: true },
                                                    label: "Request Delay (ms)",
                                                    type: "number",
                                                    min: "0",
                                                    max: "100",
                                                    density: "compact",
                                                    hint: "Wait time. 0=fastest, 50=safe",
                                                    "persistent-hint": ""
                                                  }, null, 8, ["modelValue"])
                                                ]),
                                                _: 1
                                              }),
                                              _createVNode(_component_v_col, { cols: "6" }, {
                                                default: _withCtx(() => [
                                                  _createVNode(_component_v_text_field, {
                                                    modelValue: maxRetries503.value,
                                                    "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((maxRetries503).value = $event)),
                                                    modelModifiers: { number: true },
                                                    label: "Server Busy Retries",
                                                    type: "number",
                                                    min: "0",
                                                    max: "5",
                                                    density: "compact",
                                                    hint: "Attempts on 503 errors",
                                                    "persistent-hint": ""
                                                  }, null, 8, ["modelValue"])
                                                ]),
                                                _: 1
                                              })
                                            ]),
                                            _: 1
                                          }),
                                          _cache[52] || (_cache[52] = _createElementVNode("div", { class: "text-caption text-medium-emphasis mt-2" }, [
                                            _createElementVNode("strong", null, "Request Delay:"),
                                            _createTextVNode(" 0ms = fastest but risky | 50-100ms = balanced | 200ms+ = very safe but slow"),
                                            _createElementVNode("br"),
                                            _createElementVNode("strong", null, "503 Retries:"),
                                            _createTextVNode(" Server may temporarily reject requests when overloaded. Retries help complete the request. ")
                                          ], -1))
                                        ]),
                                        _: 1
                                      }),
                                      (!canFetch.value)
                                        ? (_openBlock(), _createBlock(_component_v_alert, {
                                            key: 0,
                                            type: "warning",
                                            variant: "tonal",
                                            density: "compact",
                                            class: "mt-3",
                                            icon: "mdi-alert-circle"
                                          }, {
                                            default: _withCtx(() => [
                                              _createElementVNode("span", _hoisted_15, [
                                                _cache[53] || (_cache[53] = _createElementVNode("strong", null, "Not ready:", -1)),
                                                (!selectedRegion.value)
                                                  ? (_openBlock(), _createElementBlock("span", _hoisted_16, "Select a region (Step 1)"))
                                                  : (!selectedTimeRange.value)
                                                    ? (_openBlock(), _createElementBlock("span", _hoisted_17, "Select a time range (Step 2)"))
                                                    : _createCommentVNode("", true)
                                              ])
                                            ]),
                                            _: 1
                                          }))
                                        : (_openBlock(), _createBlock(_component_v_alert, {
                                            key: 1,
                                            type: "success",
                                            variant: "tonal",
                                            density: "compact",
                                            class: "mt-3",
                                            icon: "mdi-check-circle"
                                          }, {
                                            default: _withCtx(() => [...(_cache[54] || (_cache[54] = [
                                              _createElementVNode("span", { class: "text-caption" }, [
                                                _createElementVNode("strong", null, "Ready!"),
                                                _createTextVNode(" Fetch data or preview URLs with Dry Run")
                                              ], -1)
                                            ]))]),
                                            _: 1
                                          })),
                                      _createVNode(_component_v_btn, {
                                        color: "primary",
                                        size: "large",
                                        block: "",
                                        class: "mt-2",
                                        loading: fetching.value,
                                        disabled: !canFetch.value,
                                        onClick: fetchData,
                                        "prepend-icon": "mdi-download"
                                      }, {
                                        default: _withCtx(() => [...(_cache[55] || (_cache[55] = [
                                          _createTextVNode(" Fetch Data ", -1)
                                        ]))]),
                                        _: 1
                                      }, 8, ["loading", "disabled"]),
                                      _createVNode(_component_v_btn, {
                                        color: "secondary",
                                        variant: "outlined",
                                        block: "",
                                        class: "mt-2",
                                        loading: fetching.value,
                                        disabled: !canFetch.value,
                                        onClick: dryRunData,
                                        "prepend-icon": "mdi-test-tube"
                                      }, {
                                        default: _withCtx(() => [...(_cache[56] || (_cache[56] = [
                                          _createTextVNode(" Dry Run (Preview URLs) ", -1)
                                        ]))]),
                                        _: 1
                                      }, 8, ["loading", "disabled"])
                                    ]),
                                    _: 1
                                  })
                                ]),
                                _: 1
                              })
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_col, {
                    cols: "12",
                    lg: "5"
                  }, {
                    default: _withCtx(() => [
                      (fetching.value)
                        ? (_openBlock(), _createBlock(_component_v_card, {
                            key: 0,
                            class: "mb-3"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_card_text, { class: "py-2" }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_progress_linear, {
                                    "model-value": fetchProgress.value,
                                    color: "primary",
                                    height: "20",
                                    striped: ""
                                  }, {
                                    default: _withCtx(({ value }) => [
                                      _createElementVNode("strong", _hoisted_18, _toDisplayString(Math.ceil(value)) + "%", 1)
                                    ]),
                                    _: 1
                                  }, 8, ["model-value"]),
                                  _createElementVNode("div", _hoisted_19, _toDisplayString(completedRequests.value) + " / " + _toDisplayString(totalRequests.value) + " requests ", 1)
                                ]),
                                _: 1
                              })
                            ]),
                            _: 1
                          }))
                        : _createCommentVNode("", true),
                      _createVNode(_component_v_card, { class: "mb-3" }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_card_title, { class: "py-2" }, {
                            default: _withCtx(() => [...(_cache[57] || (_cache[57] = [
                              _createTextVNode("Time Series Data", -1)
                            ]))]),
                            _: 1
                          }),
                          _createVNode(_component_v_card_text, { class: "pa-2" }, {
                            default: _withCtx(() => [
                              (datasets.value.length > 0)
                                ? (_openBlock(), _createBlock(PlotlyGraph, {
                                    key: 0,
                                    datasets: plotlyDatasets.value,
                                    colors: plotlyColors.value,
                                    "show-errors": true,
                                    "layout-options": {legend: {y:1.15, orientation:'h', bordercolor: '#ccc', borderwidth:1}, height: 300},
                                    onClick: handleGraphClick
                                  }, null, 8, ["datasets", "colors"]))
                                : (_openBlock(), _createBlock(_component_v_alert, {
                                    key: 1,
                                    type: "info",
                                    variant: "outlined",
                                    density: "compact"
                                  }, {
                                    default: _withCtx(() => [...(_cache[58] || (_cache[58] = [
                                      _createElementVNode("strong", null, "No data yet.", -1),
                                      _createTextVNode(" Complete Steps 1-2 above, then click \"Fetch Data\". ", -1)
                                    ]))]),
                                    _: 1
                                  }))
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      }),
                      _createVNode(RequestStatistics, {
                        stats: requestStats.value,
                        onClear: clearStatistics
                      }, null, 8, ["stats"]),
                      (requestStats.value.length > 0)
                        ? (_openBlock(), _createBlock(TimeRangeStatusList, {
                            key: 1,
                            stats: requestStats.value,
                            "selected-stat": selectedTimeRangeForMap.value,
                            height: 400,
                            class: "mt-3",
                            onSelect: handleTimeRangeSelect
                          }, null, 8, ["stats", "selected-stat"]))
                        : _createCommentVNode("", true)
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }),
              _createVNode(_component_v_row, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_col, { cols: "12" }, {
                    default: _withCtx(() => [...(_cache[59] || (_cache[59] = [
                      _createElementVNode("div", { class: "text-center py-4 text-caption bg-grey-darken-4 text-grey-lighten-2" }, [
                        _createTextVNode(" Project specification by \"Human-in-the-Loop\" John Lewis 👨🏾. Interface implementation by "),
                        _createElementVNode("a", {
                          href: "https://github.com/features/copilot",
                          target: "_blank",
                          class: "text-grey-lighten-1 text-decoration-none"
                        }, "GitHub Copilot"),
                        _createTextVNode(" using "),
                        _createElementVNode("a", {
                          href: "https://www.anthropic.com/claude/sonnet",
                          target: "_blank",
                          class: "text-grey-lighten-1 text-decoration-none"
                        }, "Claude 4.5 Sonnet"),
                        _createTextVNode(" 🤖 ")
                      ], -1)
                    ]))]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createVNode(_component_v_dialog, {
        modelValue: showHelp.value,
        "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((showHelp).value = $event)),
        "max-width": "700"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_card, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_card_title, null, {
                default: _withCtx(() => [...(_cache[60] || (_cache[60] = [
                  _createTextVNode("TEMPO ESRI Service Testing Interface", -1)
                ]))]),
                _: 1
              }),
              _createVNode(_component_v_card_text, null, {
                default: _withCtx(() => [...(_cache[61] || (_cache[61] = [
                  _createElementVNode("h4", null, "Purpose", -1),
                  _createElementVNode("p", null, " Test and optimize TEMPO ESRI Image Service performance while staying under the 5000 sample-per-request limit. Features smart parceling to maximize data retrieval efficiency based on actual timestamp availability. ", -1),
                  _createElementVNode("h4", null, "Test Regions", -1),
                  _createElementVNode("ul", null, [
                    _createElementVNode("li", null, [
                      _createElementVNode("strong", null, "Small:"),
                      _createTextVNode(" 0.02° × 0.02° - Minimal spatial coverage")
                    ]),
                    _createElementVNode("li", null, [
                      _createElementVNode("strong", null, "Medium:"),
                      _createTextVNode(" 0.1° × 0.05° - Moderate spatial coverage")
                    ]),
                    _createElementVNode("li", null, [
                      _createElementVNode("strong", null, "Large:"),
                      _createTextVNode(" 1.25° × 1.0° - Large spatial coverage")
                    ])
                  ], -1),
                  _createElementVNode("h4", null, "Parceling Modes", -1),
                  _createElementVNode("ul", null, [
                    _createElementVNode("li", null, [
                      _createElementVNode("strong", null, "None:"),
                      _createTextVNode(" Use time ranges as-is (no splitting)")
                    ]),
                    _createElementVNode("li", null, [
                      _createElementVNode("strong", null, "Default:"),
                      _createTextVNode(" Fixed-size chunks based on configurable time period")
                    ]),
                    _createElementVNode("li", null, [
                      _createElementVNode("strong", null, "Smart:"),
                      _createTextVNode(" Intelligently split ranges based on actual data availability using O(T+R) algorithm")
                    ])
                  ], -1),
                  _createElementVNode("h4", null, "How to Use", -1),
                  _createElementVNode("ol", null, [
                    _createElementVNode("li", null, "Select a test region or click on the map"),
                    _createElementVNode("li", null, "Choose a time range (preset or custom)"),
                    _createElementVNode("li", null, "Configure parceling mode and sample count"),
                    _createElementVNode("li", null, "Click \"Fetch Data\" to retrieve samples"),
                    _createElementVNode("li", null, "Review console output for comprehensive statistics"),
                    _createElementVNode("li", null, "Visualize results: time series graph, expected vs actual sample markers")
                  ], -1),
                  _createElementVNode("h4", null, "Sample Count Adjustment", -1),
                  _createElementVNode("p", null, " ESRI samples on a regular grid defined by TEMPO satellite data. If your requested sample count doesn't align with the grid spacing, it will be automatically adjusted to match what ESRI would return. A warning will show the actual count and grid dimensions used (e.g., \"5 × 6 = 30 samples\"). This adjustment produces the same result as the equivalent ESRI request. ", -1),
                  _createElementVNode("h4", null, "Statistics & Visualization", -1),
                  _createElementVNode("p", null, [
                    _createElementVNode("strong", null, "Console Output:"),
                    _createTextVNode(" Check browser console for 6 sections of detailed statistics including timing, request success/failure rates, sample counts, and efficiency metrics. ")
                  ], -1),
                  _createElementVNode("p", null, [
                    _createElementVNode("strong", null, "Sample Markers:"),
                    _createTextVNode(" Toggle \"Expected Locations\" (colored by region) and \"Actual Samples\" (red) to verify ESRI returns data where expected. ")
                  ], -1)
                ]))]),
                _: 1
              }),
              _createVNode(_component_v_card_actions, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_spacer),
                  _createVNode(_component_v_btn, {
                    color: "primary",
                    onClick: _cache[10] || (_cache[10] = ($event: any) => (showHelp.value = false))
                  }, {
                    default: _withCtx(() => [...(_cache[62] || (_cache[62] = [
                      _createTextVNode("Close", -1)
                    ]))]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["modelValue"])
    ]),
    _: 1
  }))
}
}

})